import { Flex, Modal, Table, Typography } from "antd";
import { type MouseEvent, useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import PermissionCheck from "~Common/PermissionCheck";
import SearchFilter from "~components/SearchFilter/SearchFilter";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "~constants/pagination";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import type { UserRoles } from "~enums/userRoles";
import { usePermissionUserService } from "~pages/Users/hooks/usePermissionUserService.ts";
import { useGetPermissionUsers, useGetPermissionUsersTableColumns } from "~pages/Users/PermissionUserListPage/hooks";
import type { User } from "~types/user";
import InviteNotifyModal from "../components/InviteNotifyModal/InviteNotifyModal";
import { AddUserButton, UsersFilters } from "./components";
import type { UserStatuses } from "./enums/userStatuses";
import type { UserTransformed } from "./types";
import { filterUsers } from "./utilities";

const PermissionUserListPage = () => {
  const [searchText, setSearchText] = useState<string>("");
  const [roles, setRoles] = useState<Set<UserRoles>>(() => new Set());
  const [status, setStatus] = useState<UserStatuses | undefined>();
  const [inviteNotifyModalUser, setInviteNotifyModalUser] = useState<User | null>(null);
  const { formatMessage } = useIntl();
  const [modal, modalContextHolder] = Modal.useModal();
  const navigate = useNavigate();
  const handleUserAddedSuccessfully = (userData: User) => setSearchText(userData.email);

  const { data, isLoading } = useGetPermissionUsers();
  const {
    addPermissionUser,
    isAddPending: isAddUserInProgress,
    deletePermissionUser,
  } = usePermissionUserService({
    onUserAdded: handleUserAddedSuccessfully,
  });

  const onDeleteClick = (event: MouseEvent, userId: number) => {
    event.preventDefault();
    event.stopPropagation();

    modal.confirm({
      title: formatMessage({ id: "permissions.user.actions.delete.title" }),
      content: formatMessage({ id: "permissions.user.actions.delete.content" }),
      okText: formatMessage({ id: "delete" }),
      onOk: async () => deletePermissionUser(userId),
      centered: true,
    });
  };

  const onInviteNotify = useCallback((event: MouseEvent, user: UserTransformed) => {
    event.preventDefault();
    event.stopPropagation();
    setInviteNotifyModalUser(user);
  }, []);

  const { columns } = useGetPermissionUsersTableColumns(onDeleteClick, onInviteNotify);

  const filteredData = useMemo(() => filterUsers(data || [], searchText, roles, status), [data, searchText, roles, status]);

  const openUserDetails = (userId: number) => navigate(`manage/${userId}`);

  return (
    <>
      {modalContextHolder}
      <InviteNotifyModal user={inviteNotifyModalUser} onClose={() => setInviteNotifyModalUser(null)} />
      <Flex vertical gap={8}>
        <Flex justify="space-between" align="center">
          <Typography.Title level={4}>{formatMessage({ id: "permissions" })}</Typography.Title>
          <PermissionCheck permissionKeys={[PermissionNames.PERMISSIONS]} permissionOptions={[PermissionOperations.CREATE]}>
            <AddUserButton onAdd={addPermissionUser} isAddUserInProgress={isAddUserInProgress} />
          </PermissionCheck>
        </Flex>
        <Flex justify="space-between" align="center">
          <UsersFilters setRoles={setRoles} setStatus={setStatus} disabled={isLoading} />
          <SearchFilter
            searchValue={searchText}
            doSearch={setSearchText}
            placeholderIntlKey="search.two.letters"
            style={{ minWidth: 250 }}
          />
        </Flex>
        <Table
          columns={columns}
          dataSource={filteredData}
          loading={isLoading}
          onRow={(user: UserTransformed) => {
            return {
              onClick: () => openUserDetails(user.userId),
            };
          }}
          pagination={{
            pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
            defaultPageSize: 50,
            showSizeChanger: true,
            position: ["bottomCenter"],
          }}
          rowClassName="is-clickable"
          size="small"
          rowKey="userId"
        />
      </Flex>
    </>
  );
};

export default PermissionUserListPage;
